import { ref, readonly } from '@nuxtjs/composition-api';
import { Logger } from '~/helpers/logger';
import type {
  UseRecentlyViewedProductsErrors,
  UseRecentlyViewedProductsInterface,
} from './useRecentlyViewedProducts';
import { ProductInterface, Products } from '~/modules/GraphQL/types';
import useProduct from '~/modules/catalog/product/composables/useProduct';
import { useRecentlyViewedProductsStore } from '~/modules/catalog/product/store/recentlyViewedProductsStore';

/**
 * Allows getting for recently viewed products.
 *
 * See the {@link UseRecentlyViewedProductsInterface} for a list of methods and values available in this composable.
 */
export function useRecentlyViewedProducts(): UseRecentlyViewedProductsInterface {
  const loading = ref(false);
  const error = ref<UseRecentlyViewedProductsErrors>({
    load: null,
  });
  const { getProductList } = useProduct();
  const store = useRecentlyViewedProductsStore();

  const load = async ({ pageSize = 10, currentPage = 1 } = {}): Promise<ProductInterface[] | null> => {
    let results = null;

    try {
      loading.value = true;

      Logger.debug('Load recently viewed products');

      if (!store.loaded) {
        store.loadSkus();
      }

      if (store.skus.length > 0) {
        const productsList: Products = await getProductList({
          pageSize,
          currentPage,
          filter: {
            sku: {
              in: store.skus.slice(0, pageSize), // slice so that only first items will be loaded, which means latest viewed
            },
          },
        }) as Products;

        Logger.debug('[Result] Recently viewed products:', productsList);

        results = productsList?.items?.length > 0
          ? store.skus.map(sku => productsList.items.find(p => p.sku === sku)).filter(p => !!p)
          : [];
      }

      error.value.load = null;
    } catch (err) {
      error.value.load = err;
      Logger.error('useRecentlyViewedProducts/load', err);
    } finally {
      loading.value = false;
    }

    return results;
  };

  const addViewedProduct = (product: ProductInterface): void => {
    if (product.sku) {
      if (!store.loaded) {
        store.loadSkus();
      }
      store.addSku(product.sku);
    }
  }

  return {
    load,
    addViewedProduct,
    error: readonly(error),
    loading: readonly(loading),
  };
}

export * from './useRecentlyViewedProducts';
export default useRecentlyViewedProducts;
